/**
 * Unit-aware distance helpers for the line tool.
 *
 * All line-tool geometry is stored in meters; only the snapping grid, snap
 * labels, and the distance input are unit-aware. These helpers take the
 * viewer's active display unit (`viewer.lengthUnitDisplay`, shape
 * `{ code, unitspermeter }` from `LengthUnits` in defines.ts) so they work on
 * the Potree side without any React dependency.
 */

/** Minimal shape of a `LengthUnits` entry — what every helper here needs. */
export interface DisplayUnit {
  /** Suffix shown to the user: 'm', 'ft', or the inch glyph '″'. */
  code: string;
  /** Display units per meter (m: 1, ft: 3.28084, inch: 39.3701). */
  unitspermeter: number;
}

/** Inch glyph used by `LengthUnits.INCH.code` (defines.ts). */
const INCH_CODE = '″';

/**
 * Snap step expressed **in each display unit**, so the snapped value is a round
 * number of that unit as the user sees it. Picking the step in meters and
 * converting (e.g. 1 inch = 0.0833… ft) produces ugly decimals like 0.17 ft,
 * 0.25 ft, 0.33 ft — so the grid is defined per display unit instead:
 *   - meters: 0.05 m  (5 cm)
 *   - feet:   0.25 ft (3 in — comparable granularity, round in decimal feet)
 *   - inch:   1 ″
 */
const SNAP_STEP_DISPLAY: Record<string, number> = {
  m: 0.05,
  ft: 0.25,
  [INCH_CODE]: 1,
};
const DEFAULT_STEP_DISPLAY = 0.05;

/** Snap step in the given display unit (e.g. 0.25 for 'ft'). Unknown → 0.05. */
export function getSnapStepDisplay(unitCode: string): number {
  return SNAP_STEP_DISPLAY[unitCode] ?? DEFAULT_STEP_DISPLAY;
}

/** Meters → display-unit value. */
export function metersToDisplay(meters: number, unit: DisplayUnit): number {
  return meters * unit.unitspermeter;
}

/** Display-unit value → meters. */
export function displayToMeters(value: number, unit: DisplayUnit): number {
  return value / unit.unitspermeter;
}

/**
 * Quantize a metric distance to the display-unit grid and return meters.
 * Rounding happens in display units (so the value the user reads is a clean
 * multiple of the step), then converts back to meters for the geometry.
 */
export function quantizeMetersToGrid(meters: number, unit: DisplayUnit): number {
  const stepDisplay = getSnapStepDisplay(unit.code);
  const snappedDisplay = Math.round(metersToDisplay(meters, unit) / stepDisplay) * stepDisplay;
  return displayToMeters(snappedDisplay, unit);
}

/**
 * Short label for the snap indicator, formatted in the active display unit.
 * Meters keep the legacy cm/m split; feet use two decimals; inches round to
 * whole inches (the snap grid is one inch, so fractions are noise).
 */
export function formatDistanceLabel(meters: number, unit: DisplayUnit): string {
  if (unit.code === 'ft') {
    return `${metersToDisplay(meters, unit).toFixed(2)} ft`;
  }
  if (unit.code === INCH_CODE) {
    return `${Math.round(metersToDisplay(meters, unit))} ${INCH_CODE}`;
  }
  // Meters (and fallback): cm below 1 m, otherwise metres with two decimals.
  if (meters < 0.005) return '0 cm';
  if (meters < 1.0) return `${Math.round(meters * 100)} cm`;
  return `${meters.toFixed(2)} m`;
}
